import type { QuizAssignedDataWithAttempts } from '@repo/api/quiz/quizService';

import { LayoutListIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import QuizzesHeader from '@/components/features/quizzes/header/QuizzesHeader';
import QuizCard from '@/components/features/quizzes/QuizCard';
import QuizCardSkeleton from '@/components/features/quizzes/QuizCardSkeleton';
import QuizStartAlertDialog from '@/components/features/quizzes/QuizStartAlertDialog';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { InfiniteScroll } from '@/components/ui/infinite-scroll/InfiniteScroll';
import { useAssignedQuizzesData } from '@/hooks/quiz/useAssignedQuizzesData';

export default function QuizzesPage() {
  const [searchParams] = useSearchParams();
  const groupIdParam = searchParams.get('groupId');
  const groupId = groupIdParam ? Number(groupIdParam) : undefined;
  const { state, actions } = useAssignedQuizzesData({ groupId });
  const selectedQuiz = useMemo<QuizAssignedDataWithAttempts | null>(() => {
    if (state.startDialog[1]) {
      return state.data.find((item) => item.id === state.startDialog[1]) ?? null;
    }
    return null;
  }, [state.startDialog, state.data]);

  return (
    <div className="base-direction-col">
      <QuizzesHeader onSearchChange={actions.setSearchValue} filterValue={state.filterValue} onFilterChange={actions.setFilterValue} />
      <div className={'relative grid w-full grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] grid-rows-[auto] gap-3 md:gap-4'}>
        {state.isEmpty && !state.isPending && (
          <EmptyState className="col-span-full">
            <EmptyStateCircles className={'z-0'} />
            <FeaturedIcon size={'lg'} className={'z-10'}>
              <LayoutListIcon />
            </FeaturedIcon>
            <EmptyStateContent>
              <EmptyStateTitle>Brak quizów</EmptyStateTitle>
              <EmptyStateDescription>Brak quizów dostępnych w tej chwili.</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}

        {state.noResults && (
          <EmptyState className="col-span-full">
            <EmptyStateCircles className={'z-0'} />
            <FeaturedIcon size={'lg'} className={'z-10'}>
              <LayoutListIcon />
            </FeaturedIcon>
            <EmptyStateContent>
              <EmptyStateTitle>Brak wyników</EmptyStateTitle>
              <EmptyStateDescription>Brak wyników dla podanych kryteriów, spróbuj zmienić filtry lub wyszukiwanie.</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}

        {state.data.map((item) => (
          <QuizCard key={item.id} data={item} onStartCallback={() => actions.startQuiz(item.id)} />
        ))}

        {state.isLoading && (
          <>
            <QuizCardSkeleton />
            <QuizCardSkeleton />
            <QuizCardSkeleton />
            <QuizCardSkeleton />
          </>
        )}

        <InfiniteScroll hasNextPage={state.hasNextPage} isLoading={state.isLoading} isFetching={state.isFetching} next={actions.next} threshold={0.65}>
          {state.hasNextPage && !state.isError && <QuizCardSkeleton />}
        </InfiniteScroll>
      </div>

      {selectedQuiz && <QuizStartAlertDialog quiz={selectedQuiz} onStateChange={actions.setStartDialog} state={state.startDialog} />}
    </div>
  );
}
